// The winter show: one broadcast a week on one of Greg's old games, from the
// week after the 2026 World Series until spring. The nights, what each is,
// and how each night looks. Read by the gp85 pages (src/components/gp85/),
// by the search tags (src/routeMeta.js, worker.js) and by the tests, so it
// has no React in it.
//
// The games themselves are the tapes in src/booth/classics.js (titles,
// teams, the crews, the YouTube ids); this file adds the night, the look and
// the words a viewer reads. The viewer-facing words never give away how a
// game ends, the same rule as the tapes' `blurb`.
//
// Max confirmed on Oct 28 2026: Thursdays at 7:00 PM Central, the show is
// called "Pull up a seat", nights off for Thanksgiving and the holidays, the
// first night is 1985 World Series Game 5. On Nov 1 2026 he added four
// family nights after the ten: Greg's wife and each of his three daughters
// in the booth with him, one game picked for each (nights 11–14, `guest`).

import { CLASSICS } from "../booth/classics.js";
import { NEW_HOST, SWITCHED_TO_GP85 } from "../siteAddress.js";

export const SHOW_NAME = "Pull up a seat";
export const SHOW_TAGLINE = "A weekly broadcast with Greg Pryor";
// The site lives at /gp85 until the move to gp85.com; then at the root.
export const GP85_BASE = SWITCHED_TO_GP85 ? "" : "/gp85";
// "/gp85", "/gp85/album", "/gp85/games/<slug>" now; "/", "/album", "/games/<slug>"
// once the site has moved. The show's home is "/gp85", never "/gp85/".
export const gp85Path = (p = "") => `${GP85_BASE}${p}`.replace(/(.)\/$/, "$1") || "/";

// Show nights are 7:00 PM Central. Every one of them falls in standard time
// (CST, UTC-6): daylight time ends Nov 1 2026 and starts Mar 14 2027.
export const SHOW_HOUR_CT = 19;
export const SHOW_LENGTH_MIN = 150;
const CT_OFFSET = "-06:00";
export const startIso = (date) => `${date}T19:00:00${CT_OFFSET}`;
export const endIso = (date) => `${date}T21:30:00${CT_OFFSET}`;
export const startAt = (night) => Date.parse(startIso(night.date));

const chip = (bg, fg) => ({ bg, fg });
const KC = chip("#004687", "#bd9b60");
const STL = chip("#c41e3a", "#ffffff");
const NYY = chip("#0c2340", "#ffffff");
const SOX = chip("#0b1f44", "#ff6b6b");
const DET = chip("#0c2340", "#fa4616");
const SEA = chip("#0c2c56", "#ffd98a");
const BAL = chip("#df4601", "#000000");
const TOR = chip("#134a8e", "#ffffff");
const MIN = chip("#002b5c", "#ff8a99");
const MIL = chip("#0a2351", "#ffc52f");

// One night: the date, the tape, the look, the words.
//   n           1, 2, 3…, the order of the winter
//   date        the Thursday, in Central time
//   classic     the tape's id in src/booth/classics.js
//   title       the night's name (the tape's title unless the night has one)
//   guest       who's in the booth with Greg that night, if anyone:
//               { name, who } ("Storey", "Greg's middle daughter")
//   h1          the big headline on the night's page: [plain, italic]
//   when        the day the game was played, in words
//   where       the park and the town
//   chips       the two clubs' colours, away then home
//   part        Greg's part in the game, for viewers (no ending given away)
//   see         two or three cards: what you'll see that night (the home page's
//               About this game panel puts the last one's words by its button)
//   theme       the tile and the page: colours, backgrounds, a label
export const NIGHTS = [
  {
    n: 1,
    date: "2026-11-12",
    classic: "ws85-5",
    h1: ["1985 World Series,", "Game 5."],
    when: "Thursday, October 24, 1985",
    where: "Busch Stadium, St. Louis",
    chips: [KC, STL],
    standing: "Cardinals lead the Series 3–1",
    part: "Greg goes in at third base for George Brett in the 9th, throws out Jack Clark and fields Tito Landrum's infield single.",
    see: [
      {
        k: "Greg's inning",
        t: "Top of the 9th: Greg replaces George Brett at third base. A throw across to get Jack Clark, then Tito Landrum's infield single, with the Series on the line.",
      },
      {
        k: "The call on the tape",
        t: "ABC's crew, Al Michaels with Jim Palmer and Tim McCarver, the way it went out that Thursday night. Greg talks over it from his couch.",
      },
    ],
    theme: {
      label: "World Series · Game 5",
      net: "ABC Sports",
      accent: "#ffd98a",
      year: "#004687",
      tag: "#c41e3a",
      tile: "radial-gradient(70% 40% at 50% 100%, rgba(196, 30, 58, 0.55), transparent 70%), linear-gradient(160deg, #7fb4ff 0%, #004687 48%, #0b2a4e 100%)",
      page: "radial-gradient(80% 50% at 100% 0%, rgba(196, 30, 58, 0.45), transparent 60%), radial-gradient(70% 60% at 0% 20%, rgba(127, 180, 255, 0.45), transparent 60%), linear-gradient(180deg, #004687 0%, #0b2a4e 42%, #050d1a 100%)",
      scan: true,
    },
  },
  {
    n: 2,
    date: "2026-11-19",
    classic: "pine-tar",
    h1: ["The Pine Tar", "Game."],
    when: "Sunday, July 24, 1983 · finished Thursday, August 18",
    where: "Yankee Stadium, the Bronx",
    chips: [KC, NYY],
    part: "On July 24, Greg's in the Royals' bullpen with a catcher's mitt, warming up Don Hood, when George Brett's home run is taken away. The last twelve minutes of the tape are the August 18 finish, with Greg at third base in Brett's place.",
    see: [
      {
        k: "The bullpen",
        t: "Greg's warming up Don Hood with a catcher's mitt when the umpires measure the bat and take Brett's home run off the board. You'll see the bullpen's view of the Bronx losing its mind.",
      },
      {
        k: "The finish",
        t: "The last twelve minutes are August 18, when the game was finished from the top of the 9th. Greg's at third base in Brett's place, which makes this the most famous game he ever played in.",
      },
      {
        k: "The call on the tape",
        t: "The Yankees' own TV crew, Frank Messer, Phil Rizzuto, Bill White and Bobby Murcer, with Greg on the couch explaining what the bullpen saw.",
      },
    ],
    theme: {
      label: "Regular season · two dates",
      net: "Yankees TV",
      accent: "#d9a85a",
      year: "#0c2340",
      tag: "#0c2340",
      tile: "radial-gradient(70% 34% at 50% 104%, rgba(120, 78, 20, 0.7), transparent 70%), repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0 2px, transparent 2px 15px), linear-gradient(180deg, #132c4f, #061225)",
      page: "radial-gradient(60% 40% at 50% 100%, rgba(120, 78, 20, 0.75), transparent 70%), repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.075) 0 2px, transparent 2px 22px), linear-gradient(180deg, #16305a 0%, #0c2340 45%, #050d1a 100%)",
    },
  },
  {
    n: 3,
    date: "2026-12-03",
    classic: "disco",
    h1: ["Disco Demolition", "Night."],
    when: "Thursday, July 12, 1979 · Game 1 of a doubleheader",
    where: "Comiskey Park, Chicago",
    chips: [DET, SOX],
    part: "Greg starts at shortstop and doubles in the only White Sox run, with Harry Caray on the call. Then the records blow up, the fans storm the field, and the second game never gets played.",
    see: [
      {
        k: "Greg at short",
        t: "The starting shortstop in Game 1, and the double that drives in the only White Sox run of the night. Harry calls it.",
      },
      {
        k: "The night everyone remembers",
        t: "Between games the crate of records goes up and the crowd comes over the wall. Greg watched it from the field. Game 2 was never played.",
      },
      {
        k: "A footnote nobody else has",
        t: "Greg is the only player who played in both Disco Demolition Night and the Pine Tar Game. Ask him which was louder.",
      },
    ],
    theme: {
      label: "Twi-night doubleheader",
      net: "Channel 44",
      accent: "#ff9ac4",
      year: "#b1157a",
      tag: "#b1157a",
      tile: "radial-gradient(circle, rgba(255, 255, 255, 0.32) 1px, transparent 1.7px) 0 0 / 11px 11px, linear-gradient(160deg, #b1157a 0%, #3b1a6e 45%, #0c1b3a 100%)",
      page: "radial-gradient(circle, rgba(255, 255, 255, 0.28) 1px, transparent 1.8px) 0 0 / 14px 14px, radial-gradient(60% 50% at 80% 10%, rgba(255, 120, 60, 0.35), transparent 60%), linear-gradient(170deg, #b1157a 0%, #4a1c7a 38%, #0c1b3a 72%, #050d1a 100%)",
      record: true,
    },
  },
  {
    n: 4,
    date: "2026-12-10",
    classic: "munson",
    h1: ["Thurman Munson's", "last game."],
    when: "Wednesday, August 1, 1979",
    where: "Comiskey Park, Chicago",
    chips: [NYY, SOX],
    part: "Greg starts at shortstop and turns two double plays on this copy of the tape. The Yankees' starter is Don Hood, the pitcher Greg would warm up in the Pine Tar bullpen four years later. Munson died in a plane crash the next day; Greg had been in Yankees camp with him in 1977.",
    see: [
      {
        k: "Greg at short",
        t: "Two double plays turned in front of Harry and Jimmy. This copy of the tape is missing the 5th inning, which had a third one, on Reggie Jackson; Greg will tell you about it.",
      },
      {
        k: "The catcher",
        t: "Thurman Munson, the Yankees' captain, behind the plate for the last time. He was killed the next afternoon practicing landings in his plane in Canton, Ohio.",
      },
      {
        k: "Why Greg picked it",
        t: "He was in Yankees camp with Munson in 1977, the spring the Yankees made him shave. A quieter night than the others, on purpose.",
      },
    ],
    theme: {
      label: "Wednesday night · number 15",
      net: "Channel 44",
      accent: "#e8e3d6",
      year: "#0c2340",
      tag: "#0c2340",
      tile: "repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.06) 0 2px, transparent 2px 15px), linear-gradient(180deg, #0f1f3d, #050b16)",
      page: "linear-gradient(180deg, #08080c 0 14px, transparent 14px), repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0 2px, transparent 2px 22px), linear-gradient(180deg, #0f1f3d 0%, #0a1629 50%, #050d1a 100%)",
      band: true,
    },
  },
  {
    n: 5,
    date: "2026-12-17",
    classic: "sox80-0518",
    title: "Mariners at White Sox, 1980",
    h1: ["A Sunday at", "Comiskey."],
    when: "Sunday, May 18, 1980",
    where: "Comiskey Park, Chicago",
    chips: [SEA, SOX],
    part: "Greg's best day at shortstop on tape: twelve chances, three double plays and an RBI single, in a close one with Harry and Jimmy on the call. The kind of day a utility man gets remembered for, if anyone's watching.",
    see: [
      {
        k: "Twelve chances",
        t: "A shortstop's whole afternoon: twelve balls hit his way and three double plays turned. Greg will tell you which one he still thinks about.",
      },
      {
        k: "The RBI single",
        t: "He drives in a run too, in a game that stays close to the end. Bill Veeck's White Sox, two years after Veeck gave Greg his shot.",
      },
      {
        k: "Harry and Jimmy",
        t: "Harry Caray and Jimmy Piersall on Channel 44, the way Chicago heard it. Greg played for the men they were talking about.",
      },
    ],
    theme: {
      label: "Sunday · day game",
      net: "Channel 44",
      accent: "#ffd98a",
      year: "#0b1f44",
      tag: "#8a1023",
      tile: "linear-gradient(180deg, rgba(255, 220, 150, 0.22), transparent 40%), linear-gradient(160deg, #1b3f8f 0%, #0b1f44 55%, #8a1023 100%)",
      page: "linear-gradient(180deg, rgba(255, 214, 140, 0.28) 0%, transparent 34%), radial-gradient(50% 40% at 100% 70%, rgba(138, 16, 35, 0.6), transparent 70%), linear-gradient(180deg, #2a5aa8 0%, #0b1f44 48%, #050d1a 100%)",
      sun: true,
    },
  },
  {
    n: 6,
    date: "2027-01-07",
    classic: "sox81-0423",
    title: "Orioles at White Sox, 1981",
    h1: ["Three for five,", "three RBI."],
    when: "Thursday, April 23, 1981 · Game 1",
    where: "Comiskey Park, Chicago",
    chips: [BAL, SOX],
    part: "Greg's best day at the plate on tape: 3-for-5 with a double and three runs batted in, playing third base in a wild game with runs everywhere. The picture on this one is poor. The ballgame isn't.",
    see: [
      {
        k: "At the plate",
        t: "Three hits in five trips, a double, three runs driven in. Greg's best afternoon with a bat that anyone kept on tape.",
      },
      {
        k: "A wild one",
        t: "Lots of runs, both ways, early-season Comiskey with the Orioles in town. Greg's at third base in the middle of all of it.",
      },
      {
        k: "Fair warning",
        t: "The picture on this tape is the worst of the ten. Greg's memory of it is better than the video, which is the whole point of the night.",
      },
    ],
    theme: {
      label: "Game 1 · April",
      net: "WGN",
      accent: "#ff9a5c",
      year: "#0b1f44",
      tag: "#df4601",
      tile: "radial-gradient(60% 40% at 100% 0%, rgba(223, 70, 1, 0.55), transparent 70%), linear-gradient(160deg, #0b1f44 0%, #071530 60%, #4d0b17 100%)",
      page: "radial-gradient(60% 46% at 100% 0%, rgba(223, 70, 1, 0.55), transparent 65%), radial-gradient(50% 40% at 0% 100%, rgba(138, 16, 35, 0.5), transparent 70%), repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 3px), linear-gradient(180deg, #0b1f44 0%, #071530 50%, #050d1a 100%)",
    },
  },
  {
    n: 7,
    date: "2027-01-14",
    classic: "alcs84-2",
    h1: ["Eleven innings,", "and Cosell."],
    when: "Wednesday, October 3, 1984 · ALCS Game 2 · 11 innings",
    where: "Royals Stadium, Kansas City",
    chips: [DET, KC],
    part: "Greg pinch-runs for George Brett in the 10th, stays in at third base and takes Dan Quisenberry's throw to force Lance Parrish. His other postseason game, eleven innings long, with Howard Cosell on the call.",
    see: [
      {
        k: "The 10th inning",
        t: "Greg comes in to run for George Brett, then stays at third base for the rest of the night. A playoff game decided after midnight in the East.",
      },
      {
        k: "The force at third",
        t: "Dan Quisenberry fields it and throws to Greg to force Lance Parrish. Watch where Greg sets up before the pitch; he'll tell you why.",
      },
      {
        k: "Howard Cosell",
        t: "Michaels, Palmer and Cosell in the booth for ABC, in Cosell's last October calling baseball. Greg has thoughts about the delivery.",
      },
    ],
    theme: {
      label: "AL Championship Series · Game 2",
      net: "ABC Sports",
      accent: "#ffd98a",
      year: "#004687",
      tag: "#c8102e",
      tile: "radial-gradient(60% 36% at 0% 100%, rgba(250, 70, 22, 0.5), transparent 70%), linear-gradient(160deg, #0c2340 0%, #004687 50%, #072a52 100%)",
      page: "radial-gradient(60% 40% at 0% 100%, rgba(250, 70, 22, 0.5), transparent 70%), radial-gradient(50% 50% at 100% 0%, rgba(43, 138, 255, 0.35), transparent 65%), linear-gradient(180deg, #0c2340 0%, #004687 46%, #050d1a 100%)",
      lights: true,
    },
  },
  {
    n: 8,
    date: "2027-01-21",
    classic: "alcs85-7",
    h1: ["Game 7 for", "the pennant."],
    when: "Wednesday, October 16, 1985 · ALCS Game 7",
    where: "Exhibition Stadium, Toronto",
    chips: [KC, TOR],
    part: "The pennant, from the Royals' dugout, and Jim Sundberg's bases-loaded triple. The Royals had been down three games to one in this series too; Greg will tell you what that clubhouse sounded like.",
    see: [
      {
        k: "Sundberg's triple",
        t: "Bases loaded, and Jim Sundberg's ball off the top of the fence in right. Jim has sat in the booth with Greg before; a good night to have him back.",
      },
      {
        k: "From the dugout",
        t: "Greg didn't play in this one. He was on the bench for every pitch, which is its own view of a Game 7.",
      },
      {
        k: "Costas and Kubek",
        t: "NBC's crew on a cold night in Toronto. The pennant that set up the I-70 Series, and three more of these winter nights.",
      },
    ],
    theme: {
      label: "AL Championship Series · Game 7",
      net: "NBC Sports",
      accent: "#ffd98a",
      year: "#134a8e",
      tag: "#134a8e",
      tile: "linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 30%), linear-gradient(160deg, #134a8e 0%, #0b2a52 55%, #004687 100%)",
      page: "linear-gradient(180deg, rgba(255, 255, 255, 0.1), transparent 26%), radial-gradient(50% 40% at 100% 100%, rgba(0, 70, 135, 0.6), transparent 70%), linear-gradient(180deg, #1a5aa6 0%, #0b2a52 50%, #050d1a 100%)",
      cold: true,
    },
  },
  {
    n: 9,
    date: "2027-01-28",
    classic: "ws85-6",
    h1: ["The bottom of", "the 9th."],
    when: "Saturday, October 26, 1985 · World Series Game 6",
    where: "Royals Stadium, Kansas City",
    chips: [STL, KC],
    standing: "Cardinals lead the Series 3–2",
    part: "Greg's in the Royals' dugout for Don Denkinger's call and Dane Iorg's single, and Jim Sundberg scores the winning run. The most famous game he was ever in uniform for, with one more to play.",
    see: [
      {
        k: "The call at first",
        t: "Jorge Orta, Don Denkinger, and the argument that followed. Greg watched it from the top step. He'll tell you what the dugout thought it saw.",
      },
      {
        k: "Iorg's single",
        t: "Dane Iorg's bloop into right, Jim Sundberg sliding home, and the Series tied. One of the loudest nights Royals Stadium ever had.",
      },
      {
        k: "A note from Greg's book",
        t: "His book puts the Cardinals' run in the 9th. The tape says the 8th. Greg will own up to it on the air, which is half the fun.",
      },
    ],
    theme: {
      label: "World Series · Game 6",
      net: "ABC Sports",
      accent: "#ffd98a",
      year: "#004687",
      tag: "#c41e3a",
      tile: "radial-gradient(70% 40% at 50% 0%, rgba(196, 30, 58, 0.5), transparent 70%), linear-gradient(160deg, #0b2a4e 0%, #004687 50%, #7fb4ff 100%)",
      page: "radial-gradient(70% 40% at 50% 0%, rgba(196, 30, 58, 0.5), transparent 65%), radial-gradient(60% 50% at 0% 100%, rgba(127, 180, 255, 0.4), transparent 65%), linear-gradient(180deg, #0b2a4e 0%, #004687 48%, #050d1a 100%)",
      scan: true,
    },
  },
  {
    n: 10,
    date: "2027-02-04",
    classic: "ws85-7",
    h1: ["The night of", "the ring."],
    when: "Sunday, October 27, 1985 · World Series Game 7",
    where: "Royals Stadium, Kansas City",
    chips: [STL, KC],
    standing: "Series tied 3–3",
    part: "Bret Saberhagen's shutout, Darryl Motley's home run, and the clubhouse afterward. The night they won the World Series, from the Royals' dugout, with the man who wears the ring telling you where everyone was standing.",
    see: [
      {
        k: "Saberhagen",
        t: "A complete-game shutout in Game 7 of the World Series, from a 21-year-old. Greg was watching from a few feet away.",
      },
      {
        k: "Motley's homer",
        t: "Darryl Motley gets it started early, and the stadium never sits down again. Greg will point out who's on the top step for every pitch.",
      },
      {
        k: "The clubhouse",
        t: "The champagne, the trophy, the ring that came in the mail months later. Greg will have it on; ask to see it. Then his family takes the last four nights.",
      },
    ],
    theme: {
      label: "World Series · Game 7",
      net: "ABC Sports",
      accent: "#ffd98a",
      year: "#004687",
      tag: "#7a5a22",
      tile: "radial-gradient(circle at 50% 42%, transparent 54px, rgba(255, 217, 138, 0.35) 56px, rgba(255, 217, 138, 0.35) 62px, transparent 64px), linear-gradient(160deg, #f2c27b 0%, #bd9b60 36%, #004687 100%)",
      page: "radial-gradient(60% 50% at 50% 0%, rgba(242, 194, 123, 0.55), transparent 60%), radial-gradient(50% 50% at 100% 60%, rgba(43, 138, 255, 0.3), transparent 65%), linear-gradient(180deg, #7a5a22 0%, #004687 40%, #050d1a 100%)",
      gold: true,
      ring: true,
    },
  },
  // The family nights. Each guest's game was picked for her (Nov 1 2026):
  // the season Greg and Michelle were first married, the week Paige was
  // born, the Brewers for Storey (named for the quarry under Milwaukee's
  // County Stadium), and his last Opening Day for Hailey, born the winter
  // after it. First names only on the public pages.
  {
    n: 11,
    date: "2027-02-11",
    classic: "sox80-0521",
    title: "Twins at White Sox, 1980",
    guest: { name: "Michelle", who: "Greg's wife" },
    h1: ["Michelle's night", "at Comiskey."],
    when: "Wednesday, May 21, 1980",
    where: "Comiskey Park, Chicago",
    chips: [MIN, SOX],
    part: "Greg starts at shortstop, doubles home a run in the 2nd and bats again in the 9th with the game on the line. His wife, Michelle, joins him in the booth. They married after the 1979 season, so this was their first summer at Comiskey as husband and wife.",
    see: [
      {
        k: "Michelle in the booth",
        t: "They met on a charter flight between Dallas and Chicago. Ask her what she used to send down to the clubhouse during games.",
      },
      {
        k: "Greg at short",
        t: "Nine innings at shortstop, a double to right in the 2nd and a single in the 7th. The Twins' Roy Smalley is in the lineup: no hitter in Greg's career hit more balls to him.",
      },
      {
        k: "Harry and Jimmy",
        t: "Harry Caray and Jimmy Piersall on Channel 44, the summer after the wedding.",
      },
    ],
    theme: {
      label: "Wednesday night · May",
      net: "Channel 44",
      accent: "#ffb3c1",
      year: "#0b1f44",
      tag: "#c2185b",
      tile: "radial-gradient(70% 40% at 50% 0%, rgba(255, 140, 170, 0.5), transparent 70%), linear-gradient(160deg, #6b1b3d 0%, #0b1f44 55%, #050d1a 100%)",
      page: "radial-gradient(70% 45% at 85% 0%, rgba(255, 140, 170, 0.4), transparent 65%), radial-gradient(50% 40% at 0% 80%, rgba(138, 16, 35, 0.45), transparent 70%), linear-gradient(180deg, #2a1033 0%, #0b1f44 46%, #050d1a 100%)",
      lights: true,
    },
  },
  {
    n: 12,
    date: "2027-02-18",
    classic: "kc82-0409",
    title: "Tigers at Royals, 1982",
    guest: { name: "Paige", who: "Greg's oldest daughter" },
    h1: ["Paige's night:", "the 1982 opener."],
    when: "Friday, April 9, 1982 · the Royals' home opener",
    where: "Royals Stadium, Kansas City",
    chips: [DET, KC],
    part: "The Royals' first home game of 1982, sixteen days after the White Sox traded Greg to Kansas City. He didn't play. His first daughter, Paige, was born that week, and she joins him in the booth to ask him where he was.",
    see: [
      {
        k: "Paige in the booth",
        t: "A few weeks after this game, Michelle brought the baby to Royals Stadium, and the San Diego Chicken took her and danced on top of the Royals' dugout. Ask Greg if he set it up.",
      },
      {
        k: "The new man",
        t: "Greg had been a Royal for sixteen days. The Tigers in town are Jack Morris, Alan Trammell, Lou Whitaker, Lance Parrish and Kirk Gibson, the team the Royals would meet in the 1984 playoffs.",
      },
      {
        k: "The call on the tape",
        t: "Detroit's TV crew, George Kell and Al Kaline, on a 48-degree April night in Kansas City.",
      },
    ],
    theme: {
      label: "Home opener · April",
      net: "WDIV",
      accent: "#a9d8ff",
      year: "#004687",
      tag: "#fa4616",
      tile: "radial-gradient(60% 40% at 100% 0%, rgba(169, 216, 255, 0.55), transparent 70%), linear-gradient(160deg, #3d7fc4 0%, #004687 45%, #0c2340 100%)",
      page: "radial-gradient(60% 45% at 100% 0%, rgba(169, 216, 255, 0.45), transparent 65%), radial-gradient(50% 40% at 0% 100%, rgba(250, 70, 22, 0.35), transparent 70%), linear-gradient(180deg, #1d5fa3 0%, #004687 40%, #050d1a 100%)",
      cold: true,
    },
  },
  {
    n: 13,
    date: "2027-02-25",
    classic: "sox80-0730",
    title: "Brewers at White Sox, 1980",
    guest: { name: "Storey", who: "Greg's middle daughter" },
    h1: ["Storey's night:", "the Brewers."],
    when: "Wednesday, July 30, 1980 · taped from the bottom of the 6th",
    where: "Comiskey Park, Chicago",
    chips: [MIL, SOX],
    part: "The only Brewers game of Greg's on tape, joined in the bottom of the 6th, with Greg at third base against Robin Yount, Paul Molitor and Cecil Cooper. His middle daughter, Storey, joins him in the booth. She is named for the quarry that became the ground under Milwaukee's County Stadium.",
    see: [
      {
        k: "Storey in the booth",
        t: "In 1983 Greg and Michelle met a woman named Story whose grandmother had owned the quarry under County Stadium. They gave their second daughter the name, with an E. Storey was in the room with Greg the night the Brewers won the 2026 World Series.",
      },
      {
        k: "Ten days earlier",
        t: "On July 20, 1980, at County Stadium, Tony La Russa had Greg pass every pitch call from shortstop to a rookie catcher, Ricky Seilheimer. Seilheimer is catching again on this tape. Greg will tell you how it worked.",
      },
      {
        k: "Harry on the call",
        t: "Harry Caray on Channel 44 for the last three and a half innings, with Greg batting in the 7th.",
      },
    ],
    theme: {
      label: "Wednesday night · from the 6th",
      net: "Channel 44",
      accent: "#ffc52f",
      year: "#0a2351",
      tag: "#0a2351",
      tile: "radial-gradient(60% 40% at 0% 100%, rgba(255, 197, 47, 0.5), transparent 70%), linear-gradient(160deg, #13306b 0%, #0a2351 55%, #050d1a 100%)",
      page: "radial-gradient(60% 45% at 0% 100%, rgba(255, 197, 47, 0.35), transparent 65%), radial-gradient(50% 40% at 100% 0%, rgba(138, 16, 35, 0.45), transparent 70%), linear-gradient(180deg, #13306b 0%, #0a2351 45%, #050d1a 100%)",
      lights: true,
    },
  },
  {
    n: 14,
    date: "2027-03-04",
    classic: "kc86-0408",
    title: "Royals at Yankees, Opening Day 1986",
    guest: { name: "Hailey", who: "Greg's youngest daughter" },
    h1: ["Hailey's night:", "Opening Day, 1986."],
    when: "Tuesday, April 8, 1986 · Opening Day",
    where: "Yankee Stadium, the Bronx",
    chips: [KC, NYY],
    part: "The defending World Series champions open the 1986 season at Yankee Stadium, in Greg's last year in the big leagues. Greg comes off the bench in the 9th. His youngest daughter, Hailey, joins him in the booth. She talked him into writing his book and helped him write it.",
    see: [
      {
        k: "Hailey in the booth",
        t: "Greg's youngest was born the February after this season. A few weeks later he turned down a minor-league job and a coaching job to be home with Michelle and the three girls.",
      },
      {
        k: "The 9th inning",
        t: "Greg pinch-hits in the top of the 9th with the game on the line. He'll tell you what he was thinking.",
      },
      {
        k: "The call on the tape",
        t: "Phil Rizzuto and Bill White on WPIX, two of the voices from the Pine Tar tape, on the Yankees' Opening Day.",
      },
    ],
    theme: {
      label: "Opening Day · day game",
      net: "WPIX",
      accent: "#ffd98a",
      year: "#004687",
      tag: "#0c2340",
      tile: "repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.09) 0 2px, transparent 2px 15px), radial-gradient(70% 40% at 50% 0%, rgba(255, 217, 138, 0.4), transparent 70%), linear-gradient(180deg, #004687 0%, #0c2340 100%)",
      page: "radial-gradient(70% 40% at 50% 0%, rgba(255, 217, 138, 0.35), transparent 65%), repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.06) 0 2px, transparent 2px 22px), linear-gradient(180deg, #004687 0%, #0c2340 45%, #050d1a 100%)",
      sun: true,
    },
  },
];

// How many nights, in words ("fourteen"), for the copy that counts them.
const NUMBER_WORDS =
  "zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty".split(
    " ",
  );
export const nightCount = () => NUMBER_WORDS[NIGHTS.length] || String(NIGHTS.length);
export const nightCountCap = () => nightCount().replace(/^\w/, (c) => c.toUpperCase());

const byId = new Map(CLASSICS.map((c) => [c.id, c]));

// A night with its tape folded in: title, teams, tv crew, YouTube id, blurb.
// Where to watch Greg: the night's page while the tape on the air is one of
// this winter's (the page is the booth then), else the live page.
export function liveHref(booth) {
  const id = booth?.live ? booth.classic?.id : null;
  const n = id ? nightOf(id) : null;
  return n ? n.path : "/live";
}

export function nightOf(slugOrNight) {
  const night =
    typeof slugOrNight === "string" ? NIGHTS.find((n) => n.classic === slugOrNight) : slugOrNight;
  if (!night) return null;
  const tape = byId.get(night.classic);
  if (!tape) return null;
  return {
    ...night,
    slug: night.classic,
    title: night.title || tape.title,
    tape,
    teams: tape.teams,
    tv: tape.tv,
    blurb: tape.blurb,
    year: tape.date.slice(0, 4),
    path: gp85Path(`/games/${night.classic}`),
    start: startIso(night.date),
    end: endIso(night.date),
  };
}

export const nights = () => NIGHTS.map(nightOf);

// "Thu, Nov 12", "Nov 12", "Thursday, November 12"
const CT = "America/Chicago";
export const dayShort = (date) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: CT,
  }).format(new Date(startIso(date)));
export const dayBare = (date) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: CT }).format(
    new Date(startIso(date)),
  );
export const dayLong = (date) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: CT,
  }).format(new Date(startIso(date)));
export const TIME_WORDS = "7:00 PM CT";

// The night to count down to: the first one that hasn't ended yet. A night
// counts as on from first pitch until a few hours after. Null once the
// winter's over.
export function nextNight(now = Date.now()) {
  const over = SHOW_LENGTH_MIN * 60_000 + 90 * 60_000;
  return nights().find((n) => startAt(n) + over > now) || null;
}

export const prevOf = (night) => (night.n > 1 ? nightOf(NIGHTS[night.n - 2]) : null);
export const nextOf = (night) => (night.n < NIGHTS.length ? nightOf(NIGHTS[night.n]) : null);

// The whole winter, or one night, as a calendar file (RFC 5545). The link is
// a data: URL, so nothing is fetched; a tap opens the phone's or Mac's calendar.
export function calendarIcs(list = nights(), siteUrl = "") {
  const stamp = (iso) => {
    const d = new Date(iso);
    return `${d.toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`;
  };
  const esc = (s) => String(s).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:-//${NEW_HOST}//Pull up a seat//EN`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];
  for (const n of list) {
    lines.push(
      "BEGIN:VEVENT",
      `UID:gp85-${n.date}-${n.classic}@${NEW_HOST}`,
      `DTSTAMP:${stamp("2026-10-28T00:00:00Z")}`,
      `DTSTART:${stamp(n.start)}`,
      `DTEND:${stamp(n.end)}`,
      `SUMMARY:${esc(`${SHOW_NAME}: ${n.title}, with Greg Pryor`)}`,
      `DESCRIPTION:${esc(`${n.blurb} Watch free at ${siteUrl}${n.path}`)}`,
      `URL:${siteUrl}${n.path}`,
      `LOCATION:${esc(`${siteUrl}${gp85Path("/")}`)}`,
      "END:VEVENT",
    );
  }
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

export const calendarHref = (list, siteUrl) =>
  `data:text/calendar;charset=utf-8,${encodeURIComponent(calendarIcs(list, siteUrl))}`;
